/**
 * 飞书文档链接的识别与文本切分。
 *
 * RPA 发布时把文档链接按「内容」落库，展示时需要把裸链接替换成文档标题。
 * 这里只负责识别与切分，不发起请求。
 */

export interface FeishuSegment {
  type: 'text' | 'link';
  value: string;
}

/**
 * 匹配 <任意租户子域>.feishu.cn/<文档类型>/<token>，可选查询串。
 *
 * token 与查询串的字符类都不含中英文标点，因此无需额外 trim：紧跟在链接后的
 * `，` `。` `)` 等不会被吞进链接里。
 */
export const FEISHU_DOC_URL_RE =
  /https?:\/\/[a-z0-9-]+\.feishu\.cn\/(?:wiki|docx|sheets|mindnote|file)\/[A-Za-z0-9_-]+(?:\?[A-Za-z0-9\-._~%&=#/]*)?/gi;

// 非全局版本，避免 lastIndex 状态污染
const FEISHU_DOC_URL_STRICT =
  /^https?:\/\/[a-z0-9-]+\.feishu\.cn\/(?:wiki|docx|sheets|mindnote|file)\/[A-Za-z0-9_-]+(?:\?[A-Za-z0-9\-._~%&=#/]*)?$/i;

/** 是否为受支持的飞书文档链接（`/drive/home` 这类非文档路径返回 false）。 */
export const isFeishuDocUrl = (url: string): boolean =>
  typeof url === 'string' && FEISHU_DOC_URL_STRICT.test(url);

/** 去掉 query/hash，同一文档的不同查询串共用一份标题缓存。 */
export const normalizeFeishuUrl = (url: string): string => url.split(/[?#]/)[0];

/**
 * 把文本按飞书文档链接切成 text/link 片段，无损：所有片段 value 拼接等于原文。
 * 无链接时返回单个 text 片段，空串返回空数组。
 */
export function splitFeishuSegments(text: string): FeishuSegment[] {
  const segments: FeishuSegment[] = [];
  if (!text) return segments;

  const re = new RegExp(FEISHU_DOC_URL_RE.source, 'gi');
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      segments.push({ type: 'text', value: text.slice(last, match.index) });
    }
    segments.push({ type: 'link', value: match[0] });
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    segments.push({ type: 'text', value: text.slice(last) });
  }
  return segments;
}

/** 从任意文本中提取去重后的飞书文档链接（保留原始查询串）。 */
export function extractFeishuUrls(text: string): string[] {
  return Array.from(
    new Set(
      splitFeishuSegments(text)
        .filter((s) => s.type === 'link')
        .map((s) => s.value),
    ),
  );
}
